import React, { useState } from "react";
import { Box } from "@material-ui/core";
import { useSelectedTheme, useTranslate } from "../../../../../../../../Hooks";
import { getDownloadableLink } from "../../../../../../../../Helper";
import {
  DotsGrid,
  IconImageOffLine,
} from "../../../../../../../../assets/icons";
import { CustomButton } from "../../../../../../../../Components";
import { GalleryPreviewModal } from "../GalleryPreviewModal";

// styles
import useStyles from "./styles";

function ImageWithFallback({
  src,
  alt,
  className,
  fallbackClass,
  fallbackIcon,
  ...rest
}) {
  const [error, setError] = useState(false);
  return (
    <>
      {!error && src ? (
        <img
          src={src}
          alt={alt}
          className={className}
          loading="lazy"
          onError={() => setError(true)}
          {...rest}
        />
      ) : null}
      {(error || !src) && <Box className={fallbackClass}>{fallbackIcon}</Box>}
    </>
  );
}

function MarketingGallery({ PropertyImages = [] }) {
  const styles = useStyles();
  const { translate } = useTranslate("NewPropertiesView");
  const {
    theme: { palette },
  } = useSelectedTheme();

  const [IsOpenGalleryPreviewModal, setIsOpenGalleryPreviewModal] =
    useState(false);

  const mainImage = PropertyImages[0] || null;
  const verticalImage = PropertyImages[1] || null;
  const gridImages = [0, 1, 2, 3].map((i) => PropertyImages[2 + i] || null);
  const extraCount = PropertyImages.length > 6 ? PropertyImages.length - 6 : 0;

  return (
    <Box>
      <Box className={styles.galleryRoot}>
        {/* Main image */}
        <Box className={styles.mainImageBox}>
          <ImageWithFallback
            src={
              mainImage?.fileId ? getDownloadableLink(mainImage.fileId) : null
            }
            alt={mainImage?.fileName || "Main"}
            className={styles.mainImage}
            fallbackClass={styles.fallbackMain}
            fallbackIcon={
              <IconImageOffLine
                width="74"
                height="74"
                fill={palette.foreground.quinary}
              />
            }
          />
        </Box>

        {/* Vertical image */}
        <Box className={styles.verticalImageBox}>
          <ImageWithFallback
            src={
              verticalImage?.fileId
                ? getDownloadableLink(verticalImage.fileId)
                : null
            }
            alt={verticalImage?.fileName || "Vertical"}
            className={styles.verticalImage}
            fallbackClass={styles.fallbackVertical}
            fallbackIcon={
              <IconImageOffLine
                width="40"
                height="74"
                fill={palette.foreground.quinary}
              />
            }
          />
        </Box>

        {/* 2x2 grid images */}
        <Box className={styles.gridImagesBox}>
          {gridImages.map((img, idx) => {
            const isLast = idx === 3;
            return (
              <Box
                key={img?.fileId || idx}
                className={styles.gridImageWrapper}
                style={{ position: "relative" }}
              >
                <ImageWithFallback
                  src={img?.fileId ? getDownloadableLink(img.fileId) : null}
                  alt={img?.fileName || `Gallery ${idx + 3}`}
                  className={styles.gridImage}
                  fallbackClass={styles.fallbackGrid}
                  fallbackIcon={
                    <IconImageOffLine
                      width="40"
                      height="40"
                      fill={palette.foreground.quinary}
                    />
                  }
                />
                {isLast && extraCount > 0 && (
                  <Box className={styles.overlay}>
                    <Box className={styles.extraCount}>+{extraCount}</Box>
                  </Box>
                )}
                {isLast && extraCount > 0 && (
                  <CustomButton
                    className={styles.showAllPhotosBtn}
                    variant="contained"
                    color="secondary"
                    boxShadow="xs"
                    startIcon={
                      <DotsGrid
                        width="17"
                        height="17"
                        fill={palette.text.gray_700}
                      />
                    }
                    onClick={() => {
                      setIsOpenGalleryPreviewModal(true);
                    }}
                  >
                    {translate("show-all-photos")}
                  </CustomButton>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      {IsOpenGalleryPreviewModal && (
        <GalleryPreviewModal
          open={IsOpenGalleryPreviewModal}
          onClose={() => setIsOpenGalleryPreviewModal(false)}
          images={PropertyImages}
        />
      )}
    </Box>
  );
}

export default MarketingGallery;
